import React, { FC, useEffect, useRef, useState } from 'react';

interface IComponentProps {
  scrollableContainer: HTMLDivElement | null;
}

export const HorizontalScrollbar: FC<IComponentProps> = ({ scrollableContainer }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [thumbWidth, setThumbWidth] = useState<number>(80);
  const [thumbLeft, setThumbLeft] = useState<number>(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef<boolean>(false);
  const startXRef = useRef<number>(0);
  const startScrollLeftRef = useRef<number>(0);

  useEffect(() => {
    if (!scrollableContainer) {
      return;
    }

    const handleScroll = (): void => {
      const { scrollLeft, scrollWidth, clientWidth } = scrollableContainer;

      const trackWidth = scrollableContainer.clientWidth || 0;

      if (!trackWidth) {
        return;
      }

      if (scrollWidth === 0 || clientWidth === 0) {
        return;
      }

      const scrollRatio = clientWidth / scrollWidth;

      const nextThumbWidth = Math.max(scrollRatio * trackWidth, 20);

      const maxScroll = scrollWidth - clientWidth;
      const maxThumbMove = trackWidth - nextThumbWidth;

      const nextThumbLeft = (scrollLeft / maxScroll) * maxThumbMove || 0;

      setThumbWidth(nextThumbWidth);
      setThumbLeft(nextThumbLeft);
    };

    scrollableContainer.addEventListener('scroll', handleScroll);

    const resizeObserver = new ResizeObserver(() => {
      const { scrollWidth, clientWidth } = scrollableContainer;

      if (scrollWidth === 0 || clientWidth === 0) {
        return;
      }

      const hasScroll = scrollWidth > clientWidth;

      setVisible(hasScroll);

      handleScroll();
    });

    resizeObserver.observe(scrollableContainer);

    return () => {
      scrollableContainer.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [scrollableContainer]);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [thumbWidth]);

  const handleMouseMove = (e: MouseEvent): void => {
    if (!isDraggingRef.current) {
      return;
    }

    const container = scrollableContainer as HTMLDivElement;
    const trackWidth = trackRef.current?.clientWidth || 0;

    const deltaX = e.clientX - startXRef.current;

    const { scrollWidth, clientWidth } = container;

    const maxScroll = scrollWidth - clientWidth;
    const maxThumbMove = trackWidth - thumbWidth;

    const scrollDelta = (deltaX / maxThumbMove) * maxScroll;

    container.scrollLeft = startScrollLeftRef.current + scrollDelta;
  };

  const handleMouseUp = (): void => {
    isDraggingRef.current = false;
    document.body.style.userSelect = '';
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    isDraggingRef.current = true;
    startXRef.current = e.clientX;

    const container = scrollableContainer as HTMLDivElement;
    startScrollLeftRef.current = container.scrollLeft;

    document.body.style.userSelect = 'none';
  };

  return (
    <div
      ref={trackRef}
      style={{
        position: 'relative',
        width: '100%',
        height: 8,
        display: visible ? 'block' : 'none',
        background: '#eee',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: thumbWidth,
          height: '100%',
          transform: `translateX(${thumbLeft}px)`,
          background: '#999',
          borderRadius: 4,
          cursor: 'pointer',
        }}
        onMouseDown={handleMouseDown}
      />
    </div>
  );
};
